import { getActions } from './common/common.actions';
import { CommonState, initialState } from '@redux/common/common.state';
import { Injectable } from '@angular/core';
import { CommonEffects } from '@redux/common/common.effects';
import { getReducer } from '@redux/common/common.reducer';
import { getSelectors } from '@redux/common/common.selector';
import { Slide, Survey } from '@models/community/survey.model';
import { createAction, createSelector, on, props } from '@ngrx/store';
import { get } from 'lodash-es';
import { createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, from, map, mergeMap, of, toArray } from 'rxjs';
import { concatLatestFrom } from '@ngrx/operators';
import { selectAuthentication } from '@redux/authentication.redux';
import { SurveyCategory } from '@models/community/survey-category.model';

export const surveysStateKey = 'SurveysState';

export interface SurveysState extends CommonState<Survey> {
  surveys?: SurveyCategory[];
}

export const surveysState: SurveysState = {
  ...initialState<Survey>(),
  loading: true,
  surveys: [],
};
//
export const surveysActions = {
  ...getActions<Survey>(surveysStateKey),
  surveys: createAction(`[${surveysStateKey}]Surveys`, props<{ surveys: SurveyCategory[] }>()),
};
//
export const selectSurveysBase = getSelectors<Survey>(surveysStateKey);
export const selectSurveys = {
  ...selectSurveysBase,
  selectSurveys: createSelector(selectSurveysBase.selectFeature, (state) => {
    return get(state, 'surveys', []) as SurveyCategory[];
  }),
};

export const surveysReducers = getReducer<Survey>(surveysState, surveysActions, [
  on(surveysActions.surveys, (state, { surveys }) => ({
    ...state,
    surveys,
  })),
]);

@Injectable()
export class SurveysEffects extends CommonEffects<Survey> {
  constructor() {
    super();
    Object.assign(this, this.createEffects(surveysActions, selectSurveys));
  }

  surveys$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(surveysActions.collectionSuccess),
      concatLatestFrom(() => [
        this.store.select(selectAuthentication.selectRole),
        this.store.select(selectAuthentication.selectUser),
      ]),
      filter(([, role, user]) => !!role && !!user),

      mergeMap(([action, role, user]) => {
        const data = get(action, 'data', []) as Survey[];
        let surveys: Survey[] = [];
        if (data && data.length > 0) {
          const general = data.find((x) => x.reference === 'GENERAL_FEEDBACK_SURVEY');
          if (general) surveys.push(general);
          if (role === 'COMMUNITY_RESIDENT') {
            surveys = surveys.concat(
              data.filter((x) => !x.isFamilyInd && !x.reference.startsWith('MOVE_OUT')),
            );
          }
          if (role === 'COMMUNITY_RELATIVE') {
            surveys = surveys.concat(
              data.filter((x) => x.isFamilyInd && !x.reference.startsWith('MOVE_OUT')),
            );
          }
        }
        return from(surveys).pipe(
          mergeMap((survey) => {
            const path = `surveys/get-slide/${survey.surveyCorporationSchedulerId}/${user!.id}?inputMethod=WEB`;
            return this.api.get<Slide>(path).pipe(
              map((response) => {
                return { ...survey, slide: response };
              }),
              catchError((err) => {
                const completeDt = get(err, 'error.completeDt');

                if (completeDt) {
                  return of({ ...survey, completeDt, alreadyTaken: true });
                }
                return of({ ...survey, hidde: true });
              }),
            );
          }),
          toArray(), // Combina todas las respuestas en un solo array
          map((surveys) => {
            const categories: SurveyCategory[] = [];
            const general = surveys.find((x) => x.reference === 'GENERAL_FEEDBACK_SURVEY');
            if (general) categories.push({ category: '', surveys: [general] });
            //
            const news = surveys.filter(
              (x) =>
                x.reference !== 'GENERAL_FEEDBACK_SURVEY' && !!x.slide && x.slide.current === 1,
            );
            if (news && news.length) categories.push({ category: 'New', surveys: news });
            //
            const already = surveys.filter(
              (x) => x.reference !== 'GENERAL_FEEDBACK_SURVEY' && x.alreadyTaken,
            );
            if (already && already.length)
              categories.push({ category: 'Already Taken', surveys: already });

            return surveysActions.surveys({
              surveys: categories,
            });
          }),
        );
      }),
    );
  });
}
